import {
  TCardDetails,
  TCardReference,
} from '../../components/addcreditcard/credit-card-form/constants';
import { B2BCreditCardsFilterType } from '../../api/b2b/payments';
import { T2CStatus } from '../../constants/payments';
import { YesOrNo } from './account';
import { ICreditCardBrand, ICreditCardTypes } from '../common/payments';

export enum TPhaLocType {
  SHOP = 'SHOP',
  PROJECT = 'PROJECT',
}

export enum TProHouseAccountAction {
  ENROLL = 'enroll',
  UNENROLL = 'unenroll',
}
export interface IB2BPaymentCard {
  operations: IB2BPaymentOperations;
  billingAddress: IB2BBillingAddress;
  isDefault: boolean;
  cardNickName?: string;
  cardNumberLast4: string;
  cardBrand: ICreditCardBrand;
  cardExpiryMonth: string;
  cardExpiryYear: string;
  cardHolderName: string;
  ein?: string;
  encryptedAccountNumber?: string;
  agreementCode?: string;
  trackingOnly: string;
  isCardExpired: boolean;
  actions: IB2BPaymentCardActions;
  lastModifiedDate: number;
  cardStatus?: string;
  addedBy: string;
  cardBrandType: ICreditCardTypes;
  isAuthorizeFlag?: boolean;
  paymentId?: string;
  t2cPrimary: boolean;
  t2cNumber?: string | null;
  t2cStatus?: T2CStatus;
  cardCVV?: string;
  cardNumber?: string;
  routingNumber?: string;
  rewardExpDate?: string;
  rewardStartDate?: string;
  paymentType: string;
  perkTypeStatus: string;
  rewardType?: string;
  rewardUuid?: string;
  rewardDescription?: string;
  status?: string;
  gcBalance?: string;
  authorizeFlag?: boolean;
  hasValidationErrors: boolean;
  bypassRecpatcha?: boolean;
  level: string;
  fraudCheckCleared?: string;
  paypalAccountId?: string;
  paypalPayerId?: string;
  pieEncryption?: {
    pieIntegrityCheck: string;
    pieKeyId: string;
    piePhaseBit: string;
  };
  userId?: string;
  buyerId?: string;
  isActionSetAsDefault?: boolean;
  routingNumberLast4?: string;
  citiAccountId?: string;
  citiPrimary?: string;
  hdWalletAuthorized?: YesOrNo | null;
  createdDate: number;
  locName?: string;
  locType?: TPhaLocType;
  locBuyerId?: string;
}

export interface IB2BBillingAddress {
  addressId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  cityName: string | null;
  stateCode: string | null;
  countryCode: string | null;
  name: null | unknown;
  isDefault: null | unknown;
}

export interface IB2BPaymentOperations {
  showAuthStatus: string | null;
  showT2CStatus: string | null;
}

export interface IB2BPaymentCardActions {
  authorization?: boolean | null;
  removeAuthorization?: boolean | null;
  edit?: boolean | null;
  text2Confirm?: boolean | null;
  setInactive?: boolean | null;
  setActive?: boolean | null;
  viewPurchases?: boolean | null;
  setasDefault?: boolean | null;
  delete?: boolean | null;
  shareCard?: boolean | null;
  removeShare?: boolean | null;
  enrollHDWalletAuth?: boolean | null;
  removeHDWalletAuth?: boolean | null;
}

export interface ICitiCardSummaryObject {
  citiAccountId: string;
  totalNumberOfCards: number;
}

export interface IB2BPaymentsPagination {
  pn: string;
  ps: string;
  t2cNumber?: string | null;
  totalCA: number;
  totalCC: number;
  totalGC: number;
  totalKT: number;
  totalPBA: number;
  totalPGC: number;
  totalPages: string;
  totalT2CCards: number;
  totalLOC: number;
  citiCardsSummary?: ICitiCardSummaryObject[] | null;
}

export type T2CMultipleApproverContactResponse = {
  // fullName can be null when "single approver" status still exists for the customer and the data was migrated
  fullName?: string | null;
  t2cNumber: string;
  // status is derived from tender enrollment if still in "single approver" status, otherwise it will be tied to the contact.
  status: T2CStatus;
};

// maximum of 5, minimum of 1.
export type T2CMultipleApproverResponse =
  Array<T2CMultipleApproverContactResponse>;

export interface IB2BPaymentCardsResponse {
  paymentCards: {
    t2cApprovers?: T2CMultipleApproverResponse;
    paymentCard: Array<Partial<IB2BPaymentCard>>;
  };
  paginationInfo: IB2BPaymentsPagination;
}

export interface IB2BBillingAddressResponse {
  billingAddress: IB2BBillingAddress;
}

export type TB2BCreditCardUpdateResponse = Pick<
  IB2BPaymentCardsResponse,
  'paymentCards'
>;
export interface IB2BPaypalInitResponse {
  errorCode: null;
  errorDescription: null;
  token: string;
}

export interface IB2BPaypalInfo {
  orderID: string;
  payerID?: string | null;
}

export interface IB2BPaypalSaveRequest {
  token: string;
  paypalCard?: IB2BPaymentCard;
}

export interface IB2BErrorResponse {
  errors: TErrors;
}

export type TB2BGCRequest = Pick<
  IB2BPaymentCard,
  'cardNickName' | 'cardNumber' | 'isActionSetAsDefault'
>;

export type TB2BTenderTypeRequest = Pick<
  IB2BPaymentCard,
  'cardNickName' | 'cardNumber' | 'isActionSetAsDefault' | 'routingNumber'
>;

export type TB2BKeyTagRequest = Pick<
  IB2BPaymentCard,
  'cardNickName' | 'cardNumber' | 'isActionSetAsDefault' | 'cardStatus'
>;

export type TRemotePurchasesActions =
  | 'REMOVE_PHONE_AUTH'
  | 'REMOVE_T2C'
  | 'T2C'
  | 'T2C_NUMBER_UPDATE'
  | 'PHONE_AUTH_AND_T2C'
  | 'PHONE_AUTH'
  | 'REMOVE_PHONE_AUTH_AND_T2C';

export type TB2BCreditCardSearchFormValues = {
  filterValue: string;
  filterType: B2BCreditCardsFilterType;
};

export const CNP_ENROLLMENT_ACTIONS = {
  PHONE_AUTH: ['AUTHORIZE_PAA'],
  T2C: ['AUTHORIZE_PAA', 'ENROLL_T2C'],
  REMOVE_T2C: ['STOP_T2C'],
  REMOVE_PHONE_AUTH_AND_T2C: ['STOP_PAA', 'STOP_T2C'],
  REMOVE_PHONE_AUTH: ['STOP_PAA'],
} as const;

export type TCNPEnrollmentGroups = keyof typeof CNP_ENROLLMENT_ACTIONS;
export type TCNPEnrollmentActions =
  (typeof CNP_ENROLLMENT_ACTIONS)[TCNPEnrollmentGroups];

export interface IB2BCNPEnrollmentRequest {
  cnpEnrollmentRequest: {
    t2cApprovers?: T2CMultipleApproverRequest;
    paymentCard: {
      paymentId: string;
      cardCVV?: string;
      t2cNumber?: string;
      t2cPrimary?: boolean;
      cardReference?: TCardReference;
      cardDetails?: TCardDetails;
      customerIp?: string;
      forterToken?: string;
    };
    cnpEnrollmentActions: TCNPEnrollmentActions;
  };
}

export interface IB2BCNPEnrollmentResponse {
  status: 'FAILED' | 'SUCCESS' | 'PARTIAL_RESPONSE';
  paymentCard: {
    paymentId: string;
  };
  authorizePAASuccess?: null | boolean;
  enrollT2CSuccess?: null | boolean;
  stopPAASuccess?: null | boolean;
  stopT2CSuccess?: null | boolean;
  errors: IB2BErrorResponse['errors'];
}

export type TB2BPaymentCardRequest<T extends Partial<IB2BPaymentCard>> = {
  paymentCardRequest: {
    paymentCard: T;
  };
};

export type TB2BGiftCardBalanceRequest<T extends IB2BPaymentCard> = {
  paymentCardRequest: {
    paymentCard: T & {
      pin: string;
      recaptcha: string;
    };
  };
};

export type TGiftCardFormValues = Pick<
  IB2BPaymentCard,
  'cardNickName' | 'cardNumber'
>;

export type TCheckGiftCardBalanceFormValues = {
  pin: string;
  recaptcha: string;
};

export interface IB2BHDWalletUnEnrollmentResponse {
  paymentCard: {
    paymentId: string;
  };
  errors: IB2BErrorResponse['errors'];
}
export interface IB2BHDWalletUnEnrollmentRequest {
  paymentCardRequest: {
    paymentCard: {
      paymentId: string;
    };
  };
}

export interface IAuthHdWalletRequest {
  paymentCardRequest: {
    paymentCard: {
      paymentId: string;
      cardReference: TCardReference;
      cardDetails: TCardDetails;
      accountNumberXref: string;
      paymentMethodCode: string;
      posEntryMode: string;
      customerIp?: string;
      forterToken?: string;
    };
  };
}

export interface IB2BCNPHDWalletAuthResponse {
  paymentCards: {
    paymentCard: Array<Partial<IB2BPaymentCard>>;
  };
  errors?: IB2BErrorResponse['errors'];
}

export interface IAuthHdWalletWithDetailsRequest {
  paymentCardRequest: {
    paymentCard: {
      paymentId: string;
      cardNickName?: string;
      cardHolderName: string;
      posEntryMode: string;
      billingAddress: {
        stateCode: string;
        countryCode: 'US';
        addressLine1: string;
        addressLine2: string | null;
        postalCode: string;
        cityName: string;
      };
      cardReference: TCardReference;
      customerIp?: string;
      forterToken?: string;
      cardDetails: TCardDetails;
      cardExpiryMonth?: string;
      cardExpiryYear?: string;
    };
  };
}

export interface IT2CMultiResendInviteRequest {
  t2cResendInviteRequest:
    | {
        resendAllPending: false;
        resendInvites: [string]; // may include multiple invites in the future but only 1 will be allowed for MVP.
      }
    | {
        resendAllPending: true;
        resendInvites: null;
      };
}

// May be subject to change
export interface IT2CMultiResendInviteResponse {
  success: boolean;
  errors: IB2BErrorResponse['errors'];
}

export type TApprover = {
  fullName: string;
  t2cNumber: string;
  status: T2CStatus | null;
};

export type TApprovers = Array<TApprover>;

export type T2CMultipleApproverContactRequest = {
  fullName: string;
  t2cNumber: string;
};

// maximum of 5, minimum of 1.
export type T2CMultipleApproverRequest =
  Array<T2CMultipleApproverContactRequest>;

export type TB2BPHAT2CEnrollmentRequest = {
  paymentCardRequest: {
    paymentCard: {
      t2cNumber?: string;
      paymentId: string;
      t2cPrimary: boolean;
    };
    t2cApprovers?: T2CMultipleApproverRequest;
  };
};

export interface IB2BAddCheckingAccountRequest {
  addCheckingAccountPaymentCardRequest: {
    cardNickName: string;
    encryptedAccountNumber: string;
    routingNumber: string;
  };
}

export type TNewPayment = Pick<
  IB2BPaymentCard,
  | 'cardNickName'
  | 'cardNumber'
  | 'isActionSetAsDefault'
  | 'routingNumber'
  | 'cardStatus'
>;

export type TUpdateUsersForTenderRequestBody = {
  updateUsersForTenderRequest: {
    authorizeUsers?: string[];
    deauthorizeUsers?: string[];
  };
};

type TErrors = Array<{
  errorCode: string;
  errorMessage?: string;
  resourceId?: null;
}>;

export type TUpdateUsersForTenderResponse = {
  success: boolean;
  errors?: TErrors;
};

export type TAuthorizedUsersByTenderResponse = {
  userIds: string[];
};
